import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {
    Card,
    Typography,
    Descriptions,
    Button,
    message,
    Modal,
    Form,
    Select,
    DatePicker,
    TimePicker,
    Input,
    Row,
    Col,
    Divider, Space
} from "antd";
import {CheckCircleTwoTone, CloseCircleTwoTone, LeftOutlined} from "@ant-design/icons";
import api from "../../api";
import dayjs from "dayjs";
import {boldLabelStyle, maxW700, NoWrap} from "../../styles/styles";

const {Title} = Typography;
const {Option} = Select;

const TransportationDetailPage: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [transportation, setTransportation] = useState<any>(null);
    const [customers, setCustomers] = useState<any[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [arrivalEnabled, setArrivalEnabled] = useState(true);
    const [departureEnabled, setDepartureEnabled] = useState(true);
    const arrivalTransportation = Form.useWatch("arrival_transportation", form);
    const departureTransportation = Form.useWatch("departure_transportation", form);

    useEffect(() => {
        fetchTransportation();
        fetchCustomers();
    }, [id]);

    const fetchTransportation = async () => {
        try {
            const res = await api.get(`/cc/transportation/${id}/`);
            setTransportation(res.data);
            setArrivalEnabled(res.data.arrival_transportation);
            setDepartureEnabled(res.data.departure_transportation);
            form.setFieldsValue({
                ...res.data,
                arrival_transportation: res.data.arrival_transportation ? "Yes" : "No",
                departure_transportation: res.data.departure_transportation ? "Yes" : "No",
                arrival_date: res.data.arrival_date ? dayjs(res.data.arrival_date) : null,
                arrival_time: res.data.arrival_time ? dayjs(res.data.arrival_time, "HH:mm:ss") : null,
                departure_date: res.data.departure_date ? dayjs(res.data.departure_date) : null,
                departure_time: res.data.departure_time ? dayjs(res.data.departure_time, "HH:mm:ss") : null,
                departure_flight_time: res.data.departure_flight_time ? dayjs(res.data.departure_flight_time, "HH:mm:ss") : null,
                customer: res.data.customer?.id || null
            });
        } catch {
            message.error("Failed to load transportation record.");
        }
    };

    const fetchCustomers = async () => {
        try {
            const res = await api.get("/cc/customers/");
            const customerList = Array.isArray(res.data) ? res.data : res.data.results || [];
            setCustomers(customerList);
        } catch {
            message.error("Failed to load customers.");
        }
    };

    const autofillFromFlights = async () => {
        const customerId = form.getFieldValue("customer");
        const arrivalDate = form.getFieldValue("arrival_date");
        const departureDate = form.getFieldValue("departure_date");

        if (!customerId || (!arrivalDate && !departureDate)) {
            return;
        }

        try {
            const res = await api.get(`/cc/flights/?customer=${customerId}`);
            const flights = res.data.results;

            const arrivalFlight = flights.find(
                (f: any) =>
                    f.in_arrival_date === arrivalDate?.format("YYYY-MM-DD")
            );

            const departureFlight = flights.find(
                (f: any) =>
                    f.out_departure_date === departureDate?.format("YYYY-MM-DD")
            );

            if (arrivalFlight) {
                form.setFieldsValue({
                    arrival_time: arrivalFlight.in_arrival_time ? dayjs(arrivalFlight.in_arrival_time, "HH:mm:ss") : null,
                    arrival_flight: arrivalFlight.in_flight_number,
                    arrival_location: arrivalFlight.in_arrival_airport,
                });
            }

            if (departureFlight) {
                form.setFieldsValue({
                    departure_flight_time: departureFlight.out_departure_time ? dayjs(departureFlight.out_departure_time, "HH:mm:ss") : null,
                    departure_flight: departureFlight.out_flight_number,
                    departure_location: departureFlight.out_departure_airport,
                });
            }
        } catch {
            message.error("Failed to fetch flights for autofill.");
        }
    };

    const handleUpdate = async (values: any) => {
        try {
            const payload = {
                ...values,
                customer_id: values.customer,
                arrival_transportation: values.arrival_transportation === "Yes",
                departure_transportation: values.departure_transportation === "Yes",
                arrival_date: values.arrival_transportation === "Yes" ? values.arrival_date?.format("YYYY-MM-DD") : null,
                arrival_time: values.arrival_transportation === "Yes" ? values.arrival_time?.format("HH:mm:ss") : null,
                arrival_trans_pax: values.arrival_transportation === "Yes" ? values.arrival_trans_pax : null,
                arrival_location: values.arrival_transportation === "Yes" ? values.arrival_location : null,
                departure_date: values.departure_transportation === "Yes" ? values.departure_date?.format("YYYY-MM-DD") : null,
                departure_time: values.departure_transportation === "Yes" ? values.departure_time?.format("HH:mm:ss") : null,
                departure_flight_time: values.departure_transportation === "Yes" ? values.departure_flight_time?.format("HH:mm:ss") : null,
                departure_trans_pax: values.departure_transportation === "Yes" ? values.departure_trans_pax : null,
                departure_location: values.departure_transportation === "Yes" ? values.departure_location : null
            };
            await api.put(`/cc/transportation/${id}/`, payload);
            message.success("Transportation updated successfully");
            setIsModalVisible(false);
            fetchTransportation();
        } catch (err) {
            message.error("Update failed");
        }
    };

    const handleDelete = async () => {
        Modal.confirm({
            title: "Are you sure you want to delete this transportation reservation?",
            content: "This action cannot be undone.",
            okText: "Yes, delete it",
            okType: "danger",
            cancelText: "Cancel",
            centered: true,
            onOk: async () => {
                try {
                    await api.delete(`/cc/transportation/${id}/`);
                    message.success("Transportation reservation deleted");
                    navigate("/cc/transportation");
                } catch {
                    message.error("Failed to delete transportation reservation");
                }
            },
        });
    };

    const renderBooleanIcon = (value: boolean) => {
        return value ? <CheckCircleTwoTone twoToneColor="#52c41a"/> : <CloseCircleTwoTone twoToneColor="#ff4d4f"/>;
    };

    if (!transportation) return null;

    // @ts-ignore
    return (
        <div style={{padding: 24}}>
            <Button icon={<LeftOutlined/>} onClick={() => navigate(-1)} style={{marginBottom: 24}}>
                Back
            </Button>

            <Title level={3} style={{marginBottom: 16}}>
                Transportation Detail
            </Title>

            <Card
                className="rounded-2xl shadow-sm"
                title={
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={5} style={{margin: 0}}>
                                Casino Club Transportation Reservation Overview
                            </Title>
                        </Col>
                        <Col>
                            <Space>
                                <Button danger onClick={handleDelete} className="rounded-md">
                                    Delete
                                </Button>
                                <Button type="primary" onClick={() => setIsModalVisible(true)} className="rounded-md">
                                    Edit
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                }
            >
                <Descriptions
                    bordered
                    column={1}
                    style={maxW700}
                >
                    {transportation.customer && (
                        <Descriptions.Item label="Customer" labelStyle={boldLabelStyle}>
                            <NoWrap>{transportation.customer.first_name} {transportation.customer.last_name}</NoWrap>
                        </Descriptions.Item>
                    )}

                    <Descriptions.Item label="Arrival Transportation" labelStyle={boldLabelStyle}>
                        <NoWrap>{renderBooleanIcon(transportation.arrival_transportation)}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Arrival Pax" labelStyle={boldLabelStyle}>
                        <NoWrap>{transportation.arrival_transportation ? transportation.arrival_trans_pax : "n/a"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Arrival Location" labelStyle={boldLabelStyle}>
                        <NoWrap>{transportation.arrival_transportation ? transportation.arrival_location : "n/a"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Arrival Date" labelStyle={boldLabelStyle}>
                        <NoWrap>{transportation.arrival_transportation ? transportation.arrival_date : "n/a"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Arrival Time" labelStyle={boldLabelStyle}>
                        <NoWrap>{transportation.arrival_transportation ? transportation.arrival_time?.slice(0, 5) : "n/a"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Arrival Notes"
                                       labelStyle={boldLabelStyle}><NoWrap>{transportation.arrival_notes || "n/a"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Arrival Flight"
                                       labelStyle={boldLabelStyle}><NoWrap>{transportation.arrival_flight || "n/a"}</NoWrap></Descriptions.Item>

                    <Descriptions.Item label="Departure Transportation" labelStyle={boldLabelStyle}>
                        <NoWrap>{renderBooleanIcon(transportation.departure_transportation)}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Departure Pax" labelStyle={boldLabelStyle}>
                        <NoWrap>{transportation.departure_transportation ? transportation.departure_trans_pax : "n/a"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Departure Location" labelStyle={boldLabelStyle}>
                        <NoWrap>{transportation.departure_transportation ? transportation.departure_location : "n/a"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="Departure Date" labelStyle={boldLabelStyle}>
                        <NoWrap>{transportation.departure_transportation ? transportation.departure_date : "n/a"}</NoWrap>
                    </Descriptions.Item>
                    <Descriptions.Item label="VIP Lobby Departure Time" labelStyle={boldLabelStyle}>
                        <NoWrap>{transportation.departure_transportation ? transportation.departure_time?.slice(0, 5) : "n/a"}</NoWrap>
                    </Descriptions.Item>

                    <Descriptions.Item label="Departure Flight"
                                       labelStyle={boldLabelStyle}><NoWrap>{transportation.departure_flight || "n/a"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Departure Flight Time"
                                       labelStyle={boldLabelStyle}><NoWrap>{transportation.departure_transportation ? transportation.departure_flight_time?.slice(0, 5) : "n/a"}</NoWrap></Descriptions.Item>
                    <Descriptions.Item label="Departure Notes"
                                       labelStyle={boldLabelStyle}><NoWrap>{transportation.departure_notes || "n/a"}</NoWrap></Descriptions.Item>
                </Descriptions>

            </Card>

            <Modal
                title="Edit Transportation"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                centered
                width={700}
                okButtonProps={{className: "rounded-md"}}
                cancelButtonProps={{className: "rounded-md"}}
                bodyStyle={{maxHeight: "70vh", overflowY: "auto"}}
            >
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={handleUpdate}
                    onValuesChange={(changed, all) => {
                        const relevantFields = ["customer", "arrival_date", "departure_date"];
                        if (Object.keys(changed).some((key) => relevantFields.includes(key))) {
                            autofillFromFlights();
                        }
                    }}
                >
                    <Form.Item name="customer" label="Customer Name" rules={[{required: true}]}>
                        <Select
                            showSearch
                            placeholder="Select customer"
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                String(option?.children).toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {customers.map((cust) => (
                                <Option key={cust.id} value={cust.id}>
                                    {cust.first_name} {cust.last_name} ({cust.mmid})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider orientation="left">Arrival</Divider>

                    <Form.Item name="arrival_transportation" label="Arrival Transportation">
                        <Select>
                            <Option value="Yes">Yes</Option>
                            <Option value="No">No</Option>
                        </Select>
                    </Form.Item>

                    {arrivalTransportation === "Yes" && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="arrival_trans_pax" label="Arrival Pax">
                                        <Select>
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Option key={n} value={n}>
                                                    {n}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="arrival_date" label="Arrival Date">
                                        <DatePicker
                                            style={{width: "100%"}}
                                            disabledDate={(current) => current && current < dayjs().startOf("day")}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item name="arrival_location" label="Arrival Location">
                                <Select>
                                    <Option value="ICN-T1">ICN-T1</Option>
                                    <Option value="ICN-T2">ICN-T2</Option>
                                    <Option value="GMP">GMP</Option>
                                    <Option value="GMP-Private">GMP-Private</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item name="arrival_time" label="Arrival Time">
                                <TimePicker format="HH:mm" style={{width: "100%"}} allowClear/>
                            </Form.Item>
                            <Form.Item name="arrival_flight" label="Arrival Flight">
                                <Input/>
                            </Form.Item>
                            <Form.Item name="arrival_notes" label="Arrival Notes">
                                <Input.TextArea rows={2}/>
                            </Form.Item>
                        </>
                    )}

                    <Divider orientation="left">Departure</Divider>

                    <Form.Item name="departure_transportation" label="Departure Transportation">
                        <Select>
                            <Option value="Yes">Yes</Option>
                            <Option value="No">No</Option>
                        </Select>
                    </Form.Item>

                    {departureTransportation === "Yes" && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="departure_trans_pax" label="Departure Pax">
                                        <Select>
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Option key={n} value={n}>
                                                    {n}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="departure_date" label="Departure Date">
                                        <DatePicker
                                            style={{width: "100%"}}
                                            disabledDate={(current) => current && current < dayjs().startOf("day")}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item name="departure_location" label="Departure Location">
                                <Select>
                                    <Option value="ICN-T1">ICN-T1</Option>
                                    <Option value="ICN-T2">ICN-T2</Option>
                                    <Option value="GMP">GMP</Option>
                                    <Option value="GMP-Private">GMP-Private</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item name="departure_time" label="VIP Lobby Departure Time">
                                <TimePicker format="HH:mm" style={{width: "100%"}} allowClear/>
                            </Form.Item>
                            <Form.Item name="departure_flight" label="Departure Flight">
                                <Input/>
                            </Form.Item>
                            <Form.Item name="departure_flight_time" label="Flight Departure Time">
                                <TimePicker format="HH:mm" style={{width: "100%"}} allowClear/>
                            </Form.Item>
                            <Form.Item name="departure_notes" label="Departure Notes">
                                <Input.TextArea rows={2}/>
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default TransportationDetailPage;
